// Mock data for the Relationships Review screen (approved HTML:
// 09-Relationships-Review-v4-Icon-Alignment.html). Represents Doug's
// actual world: Sarah Mitchell's care team around Duke, anchored in the
// Dallas-area (Pilot Point, Aubrey, Denton, Whitesboro, Gainesville TX)
// per the approved HTML. Nova is retained as a second connected horse
// in the picker, placeholder-only, so the shape supports multiple
// horses once services/relationships returns them.
//
// Standing memory: a relationship record never authorizes outreach to
// the barn, trainer, veterinarian or farrier. All records in this file
// are design-preview only.
//
// Verification states lifted from the approved HTML and extended:
//   - "Identity not verified" (customer-reported, not yet checked)
//   - "Details to confirm" (missing fields on a placeholder record)
//   - "Verified" (identity-verification service has returned success)
//   - "Former" (connection ended, retained for history)
//
// TODO(runtime): swap mock -> real API once services land:
//   - services/relationships (confirmed service, no HTTP read endpoint)
//   - services/identity-verification (proposed, sets the Verified state)
//   - services/horse-timeline summarize call for the connected-horse card
//   - dist/account-workflows for the add/end relationship drafts

export type RelationshipStatus = 'current' | 'former';
export type VerificationState =
  | 'Identity not verified'
  | 'Details to confirm'
  | 'Verified'
  | 'Former';

export type RelationshipKind =
  | 'Barn'
  | 'Trainer'
  | 'Veterinarian'
  | 'Shoer / Farrier';

export interface Relationship {
  id: string;
  type: RelationshipKind;
  organization: string;
  contact: string;
  specialty: string;
  location: string;
  startDate: string;
  endedDate?: string;
  endReason?: string;
  notes: string;
  status: RelationshipStatus;
  verification: VerificationState;
  linkedHorseId: string;
  symbolTone?: 'default' | 'trainer' | 'veterinarian';
}

export interface RelationshipHistoryEvent {
  date: string;
  title: string;
  detail: string;
}

export interface RelationshipSummary {
  horseName: string;
  currentCount: number;
  formerSummary: string;
  currentBarn: { name: string; location: string; since: string };
  primaryVet: { name: string; practice: string };
  primaryFarrier: { name: string; cadence: string };
}

export interface ConnectedHorse {
  id: string;
  name: string;
  breed: string;
  discipline: string;
  summary: string;
  barn: string;
  trainer: string;
  primaryVet: string;
}

export interface OutsideSalesOpportunity {
  organizationName: string;
  location: string;
  manager: string;
  horsesReported: string;
  relationshipAge: string;
  customersThere: string;
  programStatus: string;
  callout: string;
}

export interface RelationshipPrompt {
  question: string;
  context: string;
  currentCount: number;
  careRoles: number;
  recentChanges: number;
}

export interface OwnershipReminder {
  repInitials: string;
  repName: string;
  message: string;
  lastTouch: string;
}

// Sarah's care team around Duke. Dallas-area addresses, real reining
// trainer / farrier / vet archetypes per the approved HTML.
export const SARAH_RELATIONSHIPS: Relationship[] = [
  {
    id: 'rel_barn_cedar_ridge',
    type: 'Barn',
    organization: 'Cedar Ridge Reining Center',
    contact: 'Madison Hall',
    specialty: 'Boarding and daily care',
    location: 'Pilot Point, TX',
    startDate: 'Jul 1, 2025',
    notes:
      'Duke is stalled at night and turned out mornings. Madison coordinates feed and supplement changes for Sarah.',
    status: 'current',
    verification: 'Identity not verified',
    linkedHorseId: 'horse_duke',
    symbolTone: 'default',
  },
  {
    id: 'rel_trainer_jamie_reed',
    type: 'Trainer',
    organization: 'Cedar Ridge Reining Center',
    contact: 'Jamie Reed',
    specialty: 'Reining · Open horses',
    location: 'Pilot Point, TX',
    startDate: 'Sep 1, 2025',
    notes:
      'Jamie rides Duke 5 days a week and manages his show schedule. Sarah wants Jamie included when the routine changes.',
    status: 'current',
    verification: 'Verified',
    linkedHorseId: 'horse_duke',
    symbolTone: 'trainer',
  },
  {
    id: 'rel_vet_morgan_reed',
    type: 'Veterinarian',
    organization: 'North Texas Performance Equine',
    contact: 'Dr. Morgan Reed, DVM',
    specialty: 'Primary veterinarian · Performance',
    location: 'Aubrey, TX',
    startDate: 'Apr 18, 2025',
    notes:
      'Primary veterinarian for routine and performance care. Sarah reported that Dr. Reed recommended continuing Gut X during show season.',
    status: 'current',
    verification: 'Verified',
    linkedHorseId: 'horse_duke',
    symbolTone: 'veterinarian',
  },
  {
    id: 'rel_vet_elena_cruz',
    type: 'Veterinarian',
    organization: 'Lone Star Equine Reproduction',
    contact: 'Dr. Elena Cruz, DVM',
    specialty: 'Reproduction specialist',
    location: 'Whitesboro, TX',
    startDate: 'Jan 9, 2026',
    notes:
      "Specialist relationship for reproductive consults only. Not Sarah's primary veterinarian.",
    status: 'current',
    verification: 'Identity not verified',
    linkedHorseId: 'horse_duke',
    symbolTone: 'veterinarian',
  },
  {
    id: 'rel_farrier_cole_bennett',
    type: 'Shoer / Farrier',
    organization: 'Bennett Performance Farrier',
    contact: 'Cole Bennett, CJF',
    specialty: 'Primary farrier · 5-week cycle',
    location: 'Pilot Point, TX',
    startDate: 'May 3, 2025',
    notes:
      'Regular shoeing and hoof-balance work. Next visit is expected around Oct 14.',
    status: 'current',
    verification: 'Identity not verified',
    linkedHorseId: 'horse_duke',
    symbolTone: 'default',
  },
  {
    id: 'rel_farrier_ryan_holt',
    type: 'Shoer / Farrier',
    organization: 'North Texas Therapeutic Shoeing',
    contact: 'Ryan Holt',
    specialty: 'Corrective shoeing · As needed',
    location: 'Denton, TX',
    startDate: 'Aug 22, 2026',
    notes:
      "Consulted for right-front heel support. Ryan is a specialist and does not replace Duke's primary farrier.",
    status: 'current',
    verification: 'Details to confirm',
    linkedHorseId: 'horse_duke',
    symbolTone: 'default',
  },
  {
    id: 'rel_trainer_taylor_brooks',
    type: 'Trainer',
    organization: 'Red River Reining',
    contact: 'Taylor Brooks',
    specialty: 'Former trainer',
    location: 'Gainesville, TX',
    startDate: 'Jan 12, 2024',
    endedDate: 'Aug 31, 2025',
    endReason: 'Sarah moved Duke to Cedar Ridge Reining Center.',
    notes:
      'Previous training relationship retained for reporting and context.',
    status: 'former',
    verification: 'Former',
    linkedHorseId: 'horse_duke',
    symbolTone: 'trainer',
  },
];

export const DUKE_SUMMARY: RelationshipSummary = {
  horseName: 'Duke',
  currentCount: 6,
  formerSummary: '1 former trainer retained in history',
  currentBarn: {
    name: 'Cedar Ridge Reining Center',
    location: 'Pilot Point, TX',
    since: 'since Jul 2025',
  },
  primaryVet: {
    name: 'Dr. Morgan Reed, DVM',
    practice: 'North Texas Performance Equine',
  },
  primaryFarrier: {
    name: 'Cole Bennett, CJF',
    cadence: '5-week cycle · next around Oct 14',
  },
};

export const DUKE_HISTORY: RelationshipHistoryEvent[] = [
  {
    date: 'Sep 30, 2026',
    title: 'Primary veterinarian reconfirmed',
    detail: 'Dr. Morgan Reed · customer reported',
  },
  {
    date: 'Aug 22, 2026',
    title: 'Corrective-shoeing specialist added',
    detail: 'Ryan Holt · right-front heel support',
  },
  {
    date: 'Sep 1, 2025',
    title: 'Trainer changed',
    detail: 'Taylor Brooks → Jamie Reed',
  },
];

export const DUKE_HISTORY_FOOTER = {
  retainedLabel: '7 dated relationship records retained',
  explainer:
    'Current and former connections stay reportable by horse, role and effective date.',
};

export const DUKE_CONNECTED: ConnectedHorse = {
  id: 'horse_duke',
  name: 'Duke',
  breed: 'Quarter Horse',
  discipline: 'Reining',
  summary: '10 years · 1,150 lb · Active show horse',
  barn: 'Cedar Ridge',
  trainer: 'Jamie Reed',
  primaryVet: 'Dr. Morgan Reed',
};

// Nova retained as a second horse Sarah owns. Placeholder only; the
// Relationships view here is scoped to Duke, Nova lives behind the
// horse picker for the add-relationship dialog.
export const NOVA_CONNECTED: ConnectedHorse = {
  id: 'horse_nova',
  name: 'Nova',
  breed: 'Appendix mare',
  discipline: 'Trail and all-around',
  summary: '7 years · 1,050 lb · Non-show, pleasure',
  barn: 'Cedar Ridge',
  trainer: 'Not recorded',
  primaryVet: 'Dr. Morgan Reed',
};

export const SARAH_HORSES: ConnectedHorse[] = [DUKE_CONNECTED, NOVA_CONNECTED];

export const CEDAR_RIDGE_OPPORTUNITY: OutsideSalesOpportunity = {
  organizationName: 'Cedar Ridge Reining Center',
  location: 'Pilot Point, TX',
  manager: 'Madison Hall, owner/manager',
  horsesReported: '18 horses reported',
  relationshipAge: '15 months',
  customersThere: '3 reported',
  programStatus: 'Not enrolled',
  callout:
    'Customer-reported connection only. Verify the business and contact before any Outside Sales outreach.',
};

export const DUKE_PROMPT: RelationshipPrompt = {
  question: 'Is this still Duke’s care team?',
  context:
    'Sarah last confirmed his primary vet and barn on Sep 30. The corrective farrier was added recently and is worth confirming during the next natural conversation.',
  currentCount: 6,
  careRoles: 4,
  recentChanges: 1,
};

export const OWNERSHIP_REMINDER: OwnershipReminder = {
  repInitials: 'JD',
  repName: 'Jamie Dorsey',
  message:
    'Sarah remains with Jamie. Relationship records provide context only and never authorize outreach to the barn, trainer, veterinarian or farrier.',
  lastTouch: 'Ownership unchanged · Last customer touch Sep 30',
};

export const RELATIONSHIP_TYPE_OPTIONS: RelationshipKind[] = [
  'Barn',
  'Trainer',
  'Veterinarian',
  'Shoer / Farrier',
];
